import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { FieldHint, Check } from '../components/ui/Field';
import { Input, InputGroup, InputAddon } from '../components/ui/Input';
import { Callout } from '../components/ui/Callout';
import { Icon } from '../components/ui/Icon';
import { AuthShell } from '../components/layout/AuthShell';

const signupSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters long'),
  confirmPassword: z.string().min(1, 'Please confirm your password'),
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: 'You must accept the Terms of Service and Privacy Policy',
  })
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type SignupFormData = z.infer<typeof signupSchema>;

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const { register: authRegister, isAuthenticated, user } = useAuth();
  const { translate } = useLanguage();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false
    }
  });

  useEffect(() => {
    if (isAuthenticated && user) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, user, navigate, from]);

  const onSubmit = async (data: SignupFormData) => {
    setError('');
    setIsLoading(true);

    try {
      await authRegister({
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        password: data.password,
        password_confirmation: data.confirmPassword,
        terms_accepted: data.acceptTerms,
      });
      navigate('/verify-email');
    } catch (err) {
      const apiMessage = (err as any)?.response?.data?.message;
      if (!apiMessage && err instanceof Error) {
        setError(err.message);
      } else {
        setError(apiMessage || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell
      title={translate('open-an-account')}
      sub={translate('free-forever')}
      footer={
        <>
          {translate('already-have-account')}{' '}
          <RouterLink to="/login" style={{ color: 'var(--brand-blue)', fontWeight: 600, textDecoration: 'none' }}>
            {translate('login')}
          </RouterLink>
        </>
      }
    >
      {error && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="warn">{error}</Callout>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* First name / Last name row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field">
            <label className="field-label" htmlFor="firstName">{translate('first-name')}</label>
            <Input
              id="firstName"
              autoComplete="given-name"
              className="input-lg"
              {...register('firstName')}
            />
            {errors.firstName && (
              <FieldHint role="alert" style={{ color: 'var(--err)' }}>
                {errors.firstName.message}
              </FieldHint>
            )}
          </div>

          <div className="field">
            <label className="field-label" htmlFor="lastName">{translate('last-name')}</label>
            <Input
              id="lastName"
              autoComplete="family-name"
              className="input-lg"
              {...register('lastName')}
            />
            {errors.lastName && (
              <FieldHint role="alert" style={{ color: 'var(--err)' }}>
                {errors.lastName.message}
              </FieldHint>
            )}
          </div>
        </div>

        {/* Work email */}
        <div className="field" style={{ marginTop: 12 }}>
          <label className="field-label" htmlFor="email">{translate('email-address')}</label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            className="input-lg"
            {...register('email')}
          />
          {errors.email && (
            <FieldHint role="alert" style={{ color: 'var(--err)' }}>
              {errors.email.message}
            </FieldHint>
          )}
        </div>

        {/* Password */}
        <div className="field" style={{ marginTop: 12 }}>
          <label className="field-label" htmlFor="password">{translate('password')}</label>
          <InputGroup>
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              className="input-lg"
              {...register('password')}
            />
            <InputAddon>
              <button
                type="button"
                style={{
                  background: 'transparent',
                  border: 0,
                  padding: '0 4px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  color: 'var(--ink-3)',
                }}
                onClick={() => setShowPassword(v => !v)}
                aria-label={translate(showPassword ? 'hide-password' : 'show-password')}
              >
                <Icon name="eye" size={16} />
              </button>
            </InputAddon>
          </InputGroup>
          {errors.password && (
            <FieldHint role="alert" style={{ color: 'var(--err)' }}>
              {errors.password.message}
            </FieldHint>
          )}
        </div>

        {/* Confirm password */}
        <div className="field" style={{ marginTop: 12 }}>
          <label className="field-label" htmlFor="confirmPassword">{translate('confirm-password')}</label>
          <InputGroup>
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              className="input-lg"
              {...register('confirmPassword')}
            />
            <InputAddon>
              <button
                type="button"
                style={{
                  background: 'transparent',
                  border: 0,
                  padding: '0 4px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  color: 'var(--ink-3)',
                }}
                onClick={() => setShowConfirmPassword(v => !v)}
                aria-label={translate(showConfirmPassword ? 'hide-password' : 'show-password')}
              >
                <Icon name="eye" size={16} />
              </button>
            </InputAddon>
          </InputGroup>
          {errors.confirmPassword && (
            <FieldHint role="alert" style={{ color: 'var(--err)' }}>
              {errors.confirmPassword.message}
            </FieldHint>
          )}
        </div>

        {/* Terms checkbox */}
        <div style={{ marginTop: 16 }}>
          <Check>
            <input type="checkbox" {...register('acceptTerms')} />
            <span style={{ fontSize: 13 }}>
              {translate('i-accept')}{' '}
              <RouterLink to="/terms" style={{ color: 'var(--brand-blue)' }}>
                {translate('terms-of-service')}
              </RouterLink>
              {' '}{translate('and')}{' '}
              <RouterLink to="/privacy" style={{ color: 'var(--brand-blue)' }}>
                {translate('privacy-policy')}
              </RouterLink>
            </span>
          </Check>
          {errors.acceptTerms && (
            <FieldHint role="alert" style={{ color: 'var(--err)', marginTop: 4, display: 'block' }}>
              {errors.acceptTerms.message}
            </FieldHint>
          )}
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-block btn-lg"
          style={{ marginTop: 18 }}
          disabled={isLoading}
        >
          {isLoading ? 'Creating account…' : translate('create-account')}
        </button>
      </form>
    </AuthShell>
  );
};

export default SignupPage;
